import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import { gr } from "../tokens";

const TOUCH = "@media (pointer: coarse), (max-width: 899.95px)";

// One chat message (messenger pattern).
// Mine: brand-soft with dark text, right-aligned, small corner bottom-right.
// Theirs: light neutral (line-2) with dark text, left-aligned, small corner bottom-left, optional avatar.
// Light fills + dark text are the easiest to read in long chats (WhatsApp / Teams pattern); use on a white pane.
// The time sits inside the bubble, bottom-right, small. `tight` = same sender as the message above
// (2px gap instead of 12px), so a run of messages reads as one block.
// `actions` (e.g. an Edit/Delete menu) shows on hover or keyboard focus, always on touch screens.
// `wide` lets embeds (video) use more width. `name` = who wrote it, on top inside the bubble
// (threads with more than two people, e.g. support replies from different staff).
export default function GrChatBubble({
  mine = false,
  name,
  avatar,
  time,
  actions,
  wide = false,
  tight = false,
  children,
  sx,
}) {
  const small = gr.space[1];
  const big = gr.radius.tile;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-end",
        justifyContent: mine ? "flex-end" : "flex-start",
        gap: gr.space[2],
        width: "100%",
        pt: tight ? "2px" : gr.space[3],
        fontFamily: gr.font.family,
        // opacity (not visibility) so the ⋮ button can still receive keyboard focus.
        "& .gr-chat-bubble-actions": { opacity: 0, transition: "opacity .15s ease" },
        "&:hover .gr-chat-bubble-actions, &:focus-within .gr-chat-bubble-actions":
          {
            opacity: 1,
          },
        [TOUCH]: { "& .gr-chat-bubble-actions": { opacity: 1 } },
        ...sx,
      }}
    >
      {!mine && avatar ? (
        // With a name on top (support threads) the photo sits next to the name, at the top;
        // without one (chat) it sits at the bottom, by the last line, as in messengers.
        <Box sx={{ flex: "none", alignSelf: name ? "flex-start" : undefined }}>
          {avatar}
        </Box>
      ) : null}
      {mine && actions ? (
        // Lined up with the bubble, muted, tint on hover.
        <Box
          className="gr-chat-bubble-actions"
          sx={{
            flex: "none",
            alignSelf: "center",
            color: gr.color.muted,
            "& button, & .MuiIconButton-root": {
              width: gr.control.sm,
              height: gr.control.sm,
              borderRadius: gr.radius.control,
            },
            "& button:hover, & .MuiIconButton-root:hover": {
              background: gr.color.tint,
              color: gr.color.ink,
            },
          }}
        >
          {actions}
        </Box>
      ) : null}
      <Box
        sx={{
          minWidth: 0,
          maxWidth: wide ? "85%" : "min(70%, 520px)",
          px: gr.space[3],
          pt: gr.space[2],
          pb: "6px",
          borderRadius: mine
            ? `${big} ${big} ${small} ${big}`
            : `${big} ${big} ${big} ${small}`,
          background: mine ? gr.color.brandSoft : gr.color.line2,
          border: "1px solid transparent",
          color: gr.color.ink,
          fontSize: gr.font.size.md,
          lineHeight: gr.font.lineHeight.normal,
          overflowWrap: "anywhere",
          wordBreak: "break-word",
          "& p": { m: 0, fontSize: "inherit", color: "inherit" },
          "& p + p": { mt: gr.space[2] },
          "& a": {
            color: gr.color.brandDark,
            textDecoration: "underline",
            textUnderlineOffset: "2px",
          },
          "& img": {
            display: "block",
            maxWidth: "100%",
            height: "auto",
            borderRadius: gr.radius.chip,
          },
          "& img + *": { mt: gr.space[2] },
          "& iframe, & video": {
            maxWidth: "100%",
            borderRadius: gr.radius.chip,
          },
          "& audio": { maxWidth: "100%" },
          "& ul, & ol": { m: 0, pl: gr.space[5] },
        }}
      >
        {name ? (
          <Box
            component="span"
            sx={{
              display: "block",
              mb: `calc(${gr.space[1]} / 2)`,
              fontSize: gr.font.size.sm,
              fontWeight: gr.font.weight.semibold,
              color: gr.color.brandDark,
            }}
          >
            {name}
          </Box>
        ) : null}
        {children}
        {time ? (
          <Box
            component="span"
            sx={{
              display: "block",
              mt: "2px",
              textAlign: "right",
              fontSize: gr.font.size.xs,
              lineHeight: 1.2,
              color: gr.color.muted,
            }}
          >
            {time}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

GrChatBubble.propTypes = {
  mine: PropTypes.bool,
  name: PropTypes.node,
  avatar: PropTypes.node,
  time: PropTypes.node,
  actions: PropTypes.node,
  wide: PropTypes.bool,
  tight: PropTypes.bool,
  children: PropTypes.node,
  sx: PropTypes.object,
};

// Day divider between messages ("12 Oct 2026").
export function GrChatDateDivider({ children }) {
  return (
    <Box
      role="separator"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: gr.space[3],
        my: gr.space[3],
        fontFamily: gr.font.family,
        fontSize: gr.font.size.xs,
        fontWeight: gr.font.weight.medium,
        color: gr.color.muted,
        "&::before, &::after": {
          content: '""',
          flex: 1,
          height: "1px",
          background: gr.color.line,
        },
      }}
    >
      {children}
    </Box>
  );
}

GrChatDateDivider.propTypes = { children: PropTypes.node };
